import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { AbstractCrudOperations, ServiceTransactionsEnum, RoleRequirements } from './abstract/abstract-crud-operations';
import { ExtractionRun, ExtractionRunCreate, ExtractionRunUpdate, Role } from './model';
import { AuthService } from './auth.service';

/** DXT-045: Acknowledgement returned by the async re-run trigger (HTTP 202). */
export interface ReextractionAck {
  documentId: string;
  status: string;
}

@Injectable({
  providedIn: 'root',
})
export class ExtractionRunService extends AbstractCrudOperations<
  ExtractionRun,
  ExtractionRunCreate,
  ExtractionRunUpdate
> {
  protected override transactions: Record<ServiceTransactionsEnum, string> = {
    [ServiceTransactionsEnum.GET_ALL]: 'extraction-runs-get-all',
    [ServiceTransactionsEnum.GET_BY_ID]: 'extraction-runs-get-by-id',
    [ServiceTransactionsEnum.CREATE]: 'extraction-runs-create',
    [ServiceTransactionsEnum.UPDATE]: 'extraction-runs-update',
    [ServiceTransactionsEnum.DELETE]: 'extraction-runs-delete',
  };

  override readonly resource = 'extraction-runs';

  protected override roleRequirements: RoleRequirements = {
    create: [Role.Admin, Role.Manager, Role.User],
    update: [Role.Admin, Role.Manager, Role.User],
    delete: [Role.Admin, Role.Manager, Role.User],
  };

  protected override authService = inject(AuthService);

  /**
   * DXT-045: Trigger an asynchronous re-run of AI extraction for an already-OCR'd document.
   * Reuses the stored OCR results (no Textract). The backend responds 202 Accepted; the new
   * extraction runs are produced asynchronously and can be fetched via getAll({ documentId }).
   *
   * @param documentId - The document to re-extract
   * @returns Observable<ReextractionAck> - Acknowledgement that extraction was accepted
   */
  triggerReextraction(documentId: string): Observable<ReextractionAck> {
    return this.apiService.post<ReextractionAck>(
      this.resource,
      { documentId },
      this.transactions[ServiceTransactionsEnum.CREATE]
    );
  }
}
